import React from 'react';
import {
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    HStack,
    Tag,
    TagLabel,
    Text,
    IconButton,
} from '@chakra-ui/react';
import { FiEdit2 } from 'react-icons/fi';
import { format } from 'date-fns';

interface CustomFieldValue {
    name: string;
    value: string;
    field_type?: string;
    category?: string;
    unit?: string;
}

interface JournalEntry {
    content: string;
    entry_date: string;
    mood_label?: string;
    tags?: string[];
    custom_fields?: CustomFieldValue[];
}

interface Tag {
    name: string;
    color: string;
}

interface JournalEntryPreviewProps {
    entries: JournalEntry[];
    tags: Tag[];
    onEditEntry?: (entry: JournalEntry) => void;
    contentPreviewLength?: number;
}

const JournalEntryPreview: React.FC<JournalEntryPreviewProps> = ({
    entries,
    tags,
    onEditEntry,
    contentPreviewLength = 100,
}) => {
    return (
        <Table variant="simple" size="sm">
            <Thead>
                <Tr>
                    <Th>Date</Th>
                    <Th>Content Preview</Th>
                    <Th>Mood</Th>
                    <Th>Tags</Th>
                    <Th>Custom Fields</Th>
                    {onEditEntry && <Th>Actions</Th>}
                </Tr>
            </Thead>
            <Tbody>
                {entries.map((entry, idx) => (
                    <Tr key={idx}>
                        <Td>{format(new Date(entry.entry_date), 'PPp')}</Td>
                        <Td>{entry.content.substring(0, contentPreviewLength)}...</Td>
                        <Td>{entry.mood_label || '-'}</Td>
                        <Td>
                            <HStack wrap="wrap">
                                {entry.tags?.map(tagName => {
                                    const tagData = tags.find(t => t.name === tagName);
                                    return (
                                        <Tag
                                            key={tagName}
                                            size="sm"
                                            colorScheme={tagData?.color || 'gray'}
                                        >
                                            <TagLabel>{tagName}</TagLabel>
                                        </Tag>
                                    );
                                })}
                            </HStack>
                        </Td>
                        <Td>
                            {entry.custom_fields?.map(field => (
                                <Text key={field.name} fontSize="sm">
                                    {field.name}: {field.value}
                                    {field.unit ? ` ${field.unit}` : ''}
                                </Text>
                            ))}
                        </Td>
                        {onEditEntry && (
                            <Td>
                                <IconButton
                                    aria-label="Edit entry"
                                    icon={<FiEdit2 />}
                                    size="sm"
                                    onClick={() => onEditEntry(entry)}
                                />
                            </Td>
                        )}
                    </Tr>
                ))}
            </Tbody>
        </Table>
    );
};

export default JournalEntryPreview; 